import type { FpsTeammate } from './FpsTeammate.js';

const MIN_INTERVAL = 8;
const MAX_INTERVAL = 15;
const MESSAGE_COOLDOWN = 14;

type CalloutKind = 'enemy' | 'cover' | 'storm' | 'reboot';

const MESSAGES: Record<CalloutKind, string> = {
  enemy: 'Enemy spotted!',
  cover: 'Cover me!',
  storm: "Storm's closing!",
  reboot: 'Need reboot!',
};

export type SquadCalloutContext = {
  teammates: FpsTeammate[];
  playerInStorm: boolean;
  squadMode: boolean;
};

/** Squadmates emit contextual callouts into the elim feed on an 8–15s cadence. */
export class FpsSquadCallouts {
  private nextAt = 4 + Math.random() * 4;
  private readonly lastByKey = new Map<string, number>();

  constructor(private readonly emit: (speaker: string, message: string) => void) {}

  update(dt: number, ctx: SquadCalloutContext): void {
    if (!ctx.squadMode || ctx.teammates.length === 0) return;

    this.nextAt -= dt;
    if (this.nextAt > 0) return;

    const now = performance.now() * 0.001;
    const pick = this.pickCallout(ctx, now);
    if (pick) {
      this.emit(pick.speaker, pick.message);
      this.lastByKey.set(`${pick.speaker}:${pick.kind}`, now);
    }

    this.nextAt = MIN_INTERVAL + Math.random() * (MAX_INTERVAL - MIN_INTERVAL);
  }

  private pickCallout(
    ctx: SquadCalloutContext,
    now: number,
  ): { speaker: string; message: string; kind: CalloutKind } | null {
    const candidates: { priority: number; speaker: string; kind: CalloutKind }[] = [];

    for (const tm of ctx.teammates) {
      const name = tm.getSquadStats().name;
      if (tm.isDead()) {
        candidates.push({ priority: 4, speaker: name, kind: 'reboot' });
      } else if (tm.isKnocked()) {
        candidates.push({ priority: 3, speaker: name, kind: 'cover' });
      } else if (tm.isShooting()) {
        candidates.push({ priority: 1, speaker: name, kind: 'enemy' });
      }
    }

    if (ctx.playerInStorm) {
      const warn = ctx.teammates.find((tm) => tm.isAlive());
      if (warn) {
        candidates.push({ priority: 2, speaker: warn.getSquadStats().name, kind: 'storm' });
      }
    }

    candidates.sort((a, b) => b.priority - a.priority);

    for (const c of candidates) {
      const key = `${c.speaker}:${c.kind}`;
      const last = this.lastByKey.get(key) ?? -999;
      if (now - last < MESSAGE_COOLDOWN) continue;
      return { speaker: c.speaker, message: MESSAGES[c.kind], kind: c.kind };
    }

    return null;
  }

  reset(): void {
    this.nextAt = MIN_INTERVAL + Math.random() * (MAX_INTERVAL - MIN_INTERVAL);
    this.lastByKey.clear();
  }
}
